import { Injectable } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { BehaviorSubject, Subject } from 'rxjs'

export interface UnsavedChangesSection {
  hasChanges: () => boolean
  onSave: () => Promise<boolean>
  onDiscard: () => void
  isSubmitting?: () => boolean
}

export interface UnsavedChangesConfig {
  form: FormGroup
  onDiscard: () => void
  onSave: () => void
  isSubmitting?: () => boolean
  // When true, the bar calls onSave even if the form is invalid — for pages whose
  // save flow does its own (often more lenient) validation, e.g. draft saves or
  // forms where only a subset of controls must be valid. Without it, the bar
  // blocks on whole-form validity.
  skipValidation?: boolean
  // Non-form state (e.g. tag/keyword arrays managed outside the FormGroup) to fold
  // into dirty detection. Read fresh on every comparison, so it always reflects "now".
  extraState?: () => unknown
}

@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
  private config$ = new BehaviorSubject<UnsavedChangesConfig | null>(null)
  readonly activeConfig$ = this.config$.asObservable()

  readonly shake$ = new Subject<void>()

  // Emitted when non-form state changes (added/removed tags, etc.) so the bar can
  // re-evaluate even though no form valueChanges fired.
  readonly changes$ = new Subject<void>()

  private initialFingerprint: string = ''
  private pageConfig: UnsavedChangesConfig | null = null
  private sections = new Set<UnsavedChangesSection>()
  private readonly sectionForm = new FormGroup({})

  register(config: UnsavedChangesConfig): void {
    this.pageConfig = config
    this.initialFingerprint = this.fingerprint(this.currentState(config))
    this.publishConfig()
  }

  snapshotInitialValue(): void {
    const config = this.pageConfig
    if (config) {
      this.initialFingerprint = this.fingerprint(this.currentState(config))
    }
  }

  unregister(): void {
    this.pageConfig = null
    this.initialFingerprint = ''
    this.publishConfig()
  }

  hasUnsavedChanges(): boolean {
    return this.pageHasChanges() || [...this.sections].some(section => section.hasChanges())
  }

  private pageHasChanges(): boolean {
    const config = this.pageConfig
    if (!config) return false
    if (!this.initialFingerprint) return false
    return this.fingerprint(this.currentState(config)) !== this.initialFingerprint
  }

  /** Add independently saved fields without replacing the page's save/discard handlers. */
  registerSection(section: UnsavedChangesSection): () => void {
    this.sections.add(section)
    this.publishConfig()
    return () => {
      this.sections.delete(section)
      this.publishConfig()
    }
  }

  private publishConfig(): void {
    const page = this.pageConfig
    if (!this.sections.size) {
      this.config$.next(page)
      return
    }
    const skipValidation = () => !this.pageHasChanges() || !!page?.skipValidation
    this.config$.next({
      form: page?.form ?? this.sectionForm,
      // A translation-only save must not validate an unchanged product/category.
      get skipValidation() {
        return skipValidation()
      },
      onSave: async () => {
        if (page?.isSubmitting?.() || [...this.sections].some(section => section.isSubmitting?.())) return
        // Finish section requests before a page save can navigate away or reload its data.
        for (const section of this.sections) {
          if (section.hasChanges() && !(await section.onSave())) return
        }
        if (this.pageHasChanges()) page?.onSave()
      },
      onDiscard: () => {
        if (page?.isSubmitting?.() || [...this.sections].some(section => section.isSubmitting?.())) return
        const dirtySections = [...this.sections].filter(section => section.hasChanges())
        dirtySections.forEach(section => section.onDiscard())
        if (this.pageHasChanges()) page?.onDiscard()
      },
      isSubmitting: () => !!page?.isSubmitting?.() || [...this.sections].some(section => section.isSubmitting?.()),
    })
  }

  // Notify listeners (the bar) that non-form state changed and dirty state should be re-checked.
  notifyChange(): void {
    this.changes$.next()
  }

  shakeBar(): void {
    this.shake$.next()
  }

  private currentState(config: UnsavedChangesConfig): unknown {
    return { form: config.form.value, extra: config.extraState ? config.extraState() : null }
  }

  private fingerprint(value: any): string {
    return JSON.stringify(this.normalize(value))
  }

  private normalize(val: any): any {
    if (val === null || val === undefined || val === '') return null
    if (typeof val === 'boolean') return val
    if (typeof val === 'number') return val
    if (val instanceof Date) return val.toISOString()
    if (typeof val === 'string') {
      const trimmed = val.trim()
      if (trimmed === '') return null
      // Treat common empty rich-text editor (Quill/PrimeNG) values as null
      if (trimmed === '<p><br></p>' || trimmed === '<p></p>' || trimmed === '<br>') return null
      return trimmed
    }
    if (Array.isArray(val)) {
      return val.map(v => this.normalize(v))
    }
    // A phone field is the same number whether it holds the string it was
    // patched with or the object ngx-intl-tel-input parses that string into.
    // The parse lands after the baseline is taken, so comparing the two shapes
    // reported an edit on a form nobody had touched.
    if (typeof val === 'object' && typeof val.e164Number === 'string') {
      return this.normalize(val.e164Number)
    }
    if (typeof val === 'object') {
      const result: any = {}
      for (const key of Object.keys(val).sort()) {
        result[key] = this.normalize(val[key])
      }
      return result
    }
    return val
  }
}
